import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { FINANCE_ROLES, holdsAny, isStaffed } from '@brickup/shared/constants'
import { DOCUMENTS } from '@brickup/shared/locales/de/documents'
import { NAV } from '@brickup/shared/locales/de/nav'
import { PLANNING } from '@brickup/shared/locales/de/planning'
import { PROJECTS } from '@brickup/shared/locales/de/projects'
import { missingRequiredDocuments } from '@brickup/shared/utils'
import { useProjectsApi } from '@/api'
import { useProjectDocumentsQuery } from '@/api/project-documents'
import { Badge, Callout, LoadingIndicator } from '@/components/base'
import { PageLayout } from '@/components/Internal/PageLayout'
import {
  NotifyCustomerModal,
  type PendingNotification,
} from '@/components/Internal/Projects/NotifyCustomerModal'
import { ProjectDetailContext } from '@/components/Internal/Projects/project-detail-context'
import { detectProjectNotificationEvent } from '@/components/Internal/Projects/project-notification-events'
import { ProjectNotesLayout } from '@/components/Internal/Projects/ProjectNotesLayout'
import type { HandleChange } from '@/components/Internal/Projects/types'
import type { Tab } from '@/components/Internal/Tabs'
import {
  useCanChangeProject,
  useCanWorkOnProject,
  useIsProjectScoped,
  type Assigned,
} from '@/hooks/use-project-access'
import { selectUserRoles, useAppStore } from '@/stores/use-app-store'

export const Route = createFileRoute('/internal/projects/$id')({
  component: ProjectDetailLayout,
})

function ProjectDetailLayout() {
  const { id } = Route.useParams()
  const pathname = useLocation({ select: location => location.pathname })
  const projectId = Number(id)

  const { mutatePatch, useGetQuery } = useProjectsApi()
  const { data: project } = useGetQuery(projectId)
  const roles = useAppStore(selectUserRoles)

  // On the shell rather than on the documents screen: the point of the badge
  // is to be read from the other tabs. Selected down to the figure, so a
  // comment, a rename or a release — none of which can change it — does not
  // re-render the whole tab subtree under this layout.
  const { data: openDocuments = 0 } = useProjectDocumentsQuery(
    projectId,
    missingRequiredDocuments,
  )

  // Carries the customer the dialog was raised for: the save snackbar offers
  // an undo, so by the time the advisor confirms, the project may belong to
  // someone else.
  const [pendingNotification, setPendingNotification] =
    useState<PendingNotification | null>(null)

  // The dialog belongs to the project it was raised on. Navigating away with
  // it open and confirming afterwards would mail the next project's customer.
  useEffect(() => setPendingNotification(null), [id])

  const handleChange = useCallback<HandleChange>(
    (key, value) => {
      if (!project) return
      // Compared against the project as it still stands — once the patch
      // lands, what it changed from is gone.
      const event = detectProjectNotificationEvent(key, value, project)
      const customerUserId =
        key === 'customerUserId' ? Number(value) : project.customerUserId

      mutatePatch(
        { id: project.id, data: { [key]: value } },
        {
          onSuccess: () => {
            if (event && customerUserId) {
              setPendingNotification({ event, customerUserId })
            }
          },
        },
      )
    },
    [project, mutatePatch],
  )

  const tabs = useMemo<Tab[]>(
    () => [
      {
        id: 'overview',
        to: '/internal/projects/$id/overview',
        params: { id },
        label: NAV.overview,
      },
      {
        id: 'documents',
        to: '/internal/projects/$id/documents',
        params: { id },
        label: NAV.documents,
        badge:
          openDocuments > 0 ? (
            <Badge
              size="sm"
              variant="warning"
              label={DOCUMENTS.openCount(openDocuments)}
            />
          ) : null,
      },
      {
        id: 'planning',
        to: '/internal/projects/$id/planning',
        params: { id },
        label: PLANNING.tabTitle,
      },
      {
        id: 'finance',
        to: '/internal/projects/$id/finance',
        params: { id },
        label: NAV.finance,
        // The architect is one of the parties paid there.
        hidden: !holdsAny(roles, FINANCE_ROLES),
      },
    ],
    [id, openDocuments, roles],
  )

  // The deepest match wins, so a tab whose path is a prefix of another cannot
  // claim it.
  const activeTab = [...tabs]
    .sort((a, b) => String(b.to).length - String(a.to).length)
    .find(tab => pathname.startsWith(String(tab.to).replace('$id', id)))?.id

  const canChange = useCanChangeProject(project)
  const canWork = useCanWorkOnProject(project)
  const isAdvisor = useIsProjectScoped()

  const contextValue = useMemo(() => {
    const property = project?.property
    if (!project || !property) return null
    return {
      project: { ...project, property },
      canChange,
      canWork,
      handleChange,
    }
  }, [project, canChange, canWork, handleChange])

  // `propertyId` is a required column and every read of this endpoint includes
  // the relation, so a loaded project without one is a backend contract break,
  // not a state to render around. Reported rather than thrown: the route-level
  // boundary would otherwise loop on retry.
  if (project && !contextValue) {
    console.error(`Project ${project.id} loaded without its property`)
  }

  if (!contextValue) return <LoadingIndicator layout="page" />

  return (
    <ProjectDetailContext.Provider value={contextValue}>
      <PageLayout
        title={contextValue.project.name}
        backTo="/internal/projects"
        tabs={tabs}
        activeTab={activeTab}
      >
        {/* For the advisor alone: whose project this is varies from row to
            row for them, and nowhere else. */}
        {isAdvisor && !canChange ? (
          <NotAssignedBanner project={contextValue.project} />
        ) : null}

        {/* What a reader may change differs from tab to tab, so each says it
            for itself. The notes take no scope: reaching this screen at all
            is an internal role, which is who writes them. */}
        <ProjectNotesLayout>
          <Outlet />
        </ProjectNotesLayout>
      </PageLayout>

      <NotifyCustomerModal
        notification={pendingNotification}
        projectId={contextValue.project.id}
        customerUnsubscribed={
          contextValue.project.customerEmailUnsubscribed === true
        }
        onClose={() => setPendingNotification(null)}
      />
    </ProjectDetailContext.Provider>
  )
}

/**
 * Why the screen under it does not take a change.
 *
 * Told apart by who is missing: a project another advisor is responsible for
 * reads differently from one nobody is, and the second is a thing to have
 * fixed rather than a thing to accept.
 */
function NotAssignedBanner({ project }: { project: Assigned }) {
  const assigned = isStaffed(project)

  return (
    <Callout
      tone="info"
      layout="bar"
      icon={Lock}
      title={
        assigned
          ? PROJECTS.notAssigned.title
          : PROJECTS.notAssigned.unassignedTitle
      }
    >
      {assigned
        ? PROJECTS.notAssigned.hint
        : PROJECTS.notAssigned.unassignedHint}
    </Callout>
  )
}
